"use client";
import { ReservationsMobilePanel } from "@/features/dashboard/components/ReservationsMobilePanel";
import { api } from "@/lib/api/api";
import { Office, ScheduleParamsAll } from "@gettactic/api";
import {
  canRespondOthers,
  canScheduleOthers
} from "@gettactic/helpers/src/permissions";
import { useAuthenticated } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@cmp-src/primitives/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { format } from "date-fns-tz";
import { NotepadText } from "lucide-react";
import { useState } from "react";

const innerClassName = "top-14 inset-y-0 right-0";

export const EmbeddedScheduleButton = ({
  currentOffice,
  onOverlayChange
}: {
  currentOffice: Office;
  onOverlayChange?: (showing: boolean) => void;
}) => {
  const [openReservationsPanel, setOpenReservationsPanel] = useState(false);
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Button
              size={"sm"}
              variant={"outline"}
              className={
                "h-[42px] rounded-lg border-neutral-200 text-neutral-1000"
              }
              onClick={() => setOpenReservationsPanel(!openReservationsPanel)}
            >
              <NotepadText />
            </Button>
          </TooltipTrigger>
          <TooltipContent>My Reservations</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {openReservationsPanel && (
        <ScheduleDrawer
          currentOffice={currentOffice}
          open={openReservationsPanel}
          setOpen={setOpenReservationsPanel}
          onOverlayChange={onOverlayChange}
        />
      )}
    </>
  );
};

const ScheduleDrawer = ({
  currentOffice,
  open,
  setOpen,
  onOverlayChange
}: {
  currentOffice: Office;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onOverlayChange?: (showing: boolean) => void;
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [weekDate, setWeekDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const hasScheduler = canScheduleOthers(authenticatedUser);
  const canApprove = canRespondOthers(authenticatedUser);

  const commonParams: ScheduleParamsAll = {
    type: ["desk", "workspace", "meeting_room", "parking_space", "parking_lot"],
    date: weekDate,
    office: [currentOffice.id ?? ""],
    tz: currentOffice.time_zone
  };

  const questionnaireData = useQuery({
    queryKey: ["organizations.questionnaire", currentOffice.id],

    queryFn: async () => {
      const res = await api.client.organizations.questionnaire(
        currentOffice.id ?? ""
      );
      return res.result;
    },

    enabled: !!currentOffice.id
  });
  const scheduleMeData = useQuery({
    queryKey: ["schedules", "me", commonParams],

    queryFn: async () => {
      const res = await api.client.schedules.allIndexed("me", commonParams);
      return res.result;
    },
    enabled: !!currentOffice.id
  });
  const schedulerData = useQuery({
    queryKey: ["schedules", "managed", commonParams],

    queryFn: async () => {
      const res = await api.client.schedules.allIndexed(
        "managed",
        commonParams
      );
      return res.result;
    },

    enabled: !!currentOffice?.id && hasScheduler
  });

  const schedulePendingData = useQuery({
    queryKey: ["schedules", "pending", commonParams],

    queryFn: async () => {
      const res = await api.client.schedules.allIndexed(
        "pending",
        commonParams
      );
      return res.result;
    },

    enabled: !!currentOffice?.id && !!canApprove
  });

  const scheduleOccupiedData = useQuery({
    queryKey: ["schedules", "occupied", commonParams],

    queryFn: async () => {
      const res = await api.client.schedules.allIndexed(
        "occupied",
        commonParams
      );
      return res.result;
    },
    enabled: !!currentOffice.id
  });

  const [zIndex, setZIndex] = useState<number | undefined>(1200);
  const onShowingAnotherModal = (showing: boolean) => {
    onOverlayChange?.(showing);
    if (showing) {
      // Drop drawer below dialog overlay (z ~ 250) and keep top card above (z-sticky 1100)
      setZIndex(200);
    } else {
      // Keep drawer above top card when no nested modal is open
      setZIndex(1200);
    }
  };
  return (
    <>
      {currentOffice.id &&
      questionnaireData.isSuccess &&
      questionnaireData.data &&
      scheduleMeData.isSuccess &&
      scheduleMeData.data &&
      // we require the schedulerData only if user has the correct perms
      (!hasScheduler || (schedulerData.isSuccess && schedulerData.data)) &&
      (!canApprove ||
        (schedulePendingData.isSuccess && schedulePendingData.data)) &&
      scheduleOccupiedData.isSuccess &&
      scheduleOccupiedData.data ? (
        <ReservationsMobilePanel
          zIndex={zIndex}
          innerClassName={innerClassName}
          onShowingAnotherModal={onShowingAnotherModal}
          weekDate={weekDate}
          setWeekDate={setWeekDate}
          embedded={true}
          setOpen={setOpen}
          open={open}
          user={authenticatedUser}
          officeId={currentOffice.id}
          questionnaireData={questionnaireData.data}
          scheduleMeData={scheduleMeData.data}
          schedulerData={schedulerData.data ?? null}
          schedulePendingData={schedulePendingData.data ?? null}
          canApprove={!!canApprove}
          scheduleOccupiedData={scheduleOccupiedData.data}
        />
      ) : null}
    </>
  );
};
